import { noop } from '@utils/noop';
import { Dispatch, createContext, useContext } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ObjectSchema, boolean, date, number, object, string } from 'yup';

export type ProviderConfig = {
  minBet: number;
  maxBet: number;
  minAutoCashout: number | null;
  maxAutoCashout: number | null;
  currency: string;
  isRoundable: boolean;
};

export type CreateRaindropFormState = {
  name: string;
  providerId: string;
  casinoId: string | null;
  showCasinoId: boolean | null;
  timezone: string;
  startDate: Date | string | null;
  endDate: Date | string | null;
  distributionFrom: string | null;
  distributionTo: string | null;
  freebetsPerRain: number | null;
  rainsPerDay: number | null;
  freebetValue: number | null;
  currency: string;
  claimLimitNumber: number | null;
  claimLimitPeriod: string | null;
  minMultiplier: number | null;
  freebetValidityDate: Date | string | null;
  claimDeadlineMinutes: number | null;
  playerMinBalance: number | null;
  playerMinWager: number | null;
  wagerPeriod: number | null;
  dayBudget: number | null;
  totalBudget: number | null;
};

const parseHHMMToMinutes = (value: unknown): number | null => {
  if (typeof value !== 'string') return null;
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
  if (!match) return null;
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  return hours * 60 + minutes;
};

const isEmptyValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (typeof value === 'number') {
    if (value === 0) return true;
    if (Number.isNaN(value)) return true;
  }
  return false;
};

export const buildCreateRaindropFormStateDefault = (): CreateRaindropFormState => {
  const now = new Date();
  const today = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    now.getHours(),
    now.getMinutes(),
    now.getSeconds(),
    now.getMilliseconds()
  );
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  return {
    name: '',
    providerId: '',
    casinoId: null,
    showCasinoId: null,
    timezone: '',
    startDate: today.toISOString(),
    endDate: today.toISOString(),
    distributionFrom: '',
    distributionTo: '',
    freebetsPerRain: null,
    rainsPerDay: null,
    freebetValue: null,
    currency: '',
    claimLimitNumber: null,
    claimLimitPeriod: null,
    minMultiplier: null,
    freebetValidityDate: tomorrow.toISOString(),
    claimDeadlineMinutes: null,
    playerMinBalance: null,
    playerMinWager: null,
    wagerPeriod: null,
    dayBudget: 0,
    totalBudget: 0,
  };
};

export const createRaindropFormStateDefault: CreateRaindropFormState =
  buildCreateRaindropFormStateDefault();

export const createRaindropSchema = (
  providerConfigs?: ProviderConfig[]
): ObjectSchema<Partial<CreateRaindropFormState>> => {
  return object<Partial<CreateRaindropFormState>>().shape({
    name: string()
      .required('name is required')
      .min(3, 'name must be at least 3 characters')
      .max(100, 'name must be at most 100 characters'),
    providerId: string().required('operator is required'),
    showCasinoId: boolean().nullable().notRequired(),
    timezone: string()
      .required('timezone is required')
      .matches(/^UTC[+-](0\d|1[0-4]):[0-5]\d$/, 'timezone must be in UTC±HH:MM format'),
    casinoId: string()
      .nullable()
      .transform((value) => {
        if (typeof value === 'string') {
          return value.trim();
        }
        return value;
      })
      .when('showCasinoId', {
        is: true,
        then: (schema) =>
          schema
            .required('casino ID is required when multiple operators is selected')
            .test('no-spaces', 'Casino ID cannot contain spaces', function (value) {
              if (!value || typeof value !== 'string') {
                return true;
              }
              return !value.includes(' ');
            }),
        otherwise: (schema) => schema.notRequired(),
      }),
    startDate: date()
      .required('start date is required')
      .nullable()
      .typeError('start date must be a valid date'),
    endDate: date()
      .required('end date is required')
      .nullable()
      .typeError('end date must be a valid date')
      .when('startDate', (startDate, schema) => {
        if (startDate && startDate[0]) {
          return schema.min(startDate[0], 'End Date must be greater than or equal to Start Date');
        }
        return schema;
      }),
    distributionFrom: string()
      .required('distribution time from is required')
      .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, 'distribution time from must be in HH:MM format')
      .typeError('distribution time from must be a string')
      .test(
        'min-interval-between-rains',
        'The interval between rains must be at least 5 minutes. Increase the distribution window or decrease the number of rains per day.',
        function validateMinInterval(value) {
          const { distributionTo, rainsPerDay } = this.parent as Partial<CreateRaindropFormState>;
          const fromMinutes = parseHHMMToMinutes(value);
          const toMinutes = parseHHMMToMinutes(distributionTo);

          if (fromMinutes === null || toMinutes === null || !rainsPerDay || rainsPerDay <= 0) {
            return true;
          }

          const intervalMinutes = toMinutes - fromMinutes;
          if (intervalMinutes <= 0) {
            return true;
          }

          const intervalPerRain = intervalMinutes / rainsPerDay;
          return intervalPerRain >= 5;
        }
      ),
    distributionTo: string()
      .required('distribution time to is required')
      .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, 'distribution time to must be in HH:MM format')
      .typeError('distribution time to must be a string')
      .test(
        'is-after-distribution-from',
        'Distribution To must be later than Distribution From',
        function validateDistributionTo(value) {
          const { distributionFrom } = this.parent as Partial<CreateRaindropFormState>;

          const toMinutes = parseHHMMToMinutes(value);
          const fromMinutes = parseHHMMToMinutes(distributionFrom);

          if (toMinutes === null || fromMinutes === null) {
            return true;
          }

          return toMinutes > fromMinutes;
        }
      )
      .test(
        'min-interval-between-rains',
        'The interval between rains must be at least 5 minutes. Increase the distribution window or decrease the number of rains per day.',
        function validateMinInterval(value) {
          const { distributionFrom, rainsPerDay } = this.parent as Partial<CreateRaindropFormState>;
          const toMinutes = parseHHMMToMinutes(value);
          const fromMinutes = parseHHMMToMinutes(distributionFrom);

          if (toMinutes === null || fromMinutes === null || !rainsPerDay || rainsPerDay <= 0) {
            return true;
          }

          const intervalMinutes = toMinutes - fromMinutes;
          if (intervalMinutes <= 0) {
            return true;
          }

          const intervalPerRain = intervalMinutes / rainsPerDay;
          return intervalPerRain >= 5;
        }
      ),
    freebetsPerRain: number()
      .required('number of freebets per rain is required')
      .integer('number of freebets per rain must be an integer')
      .min(1, 'freebets per rain must be at least 1')
      .max(500, 'freebets per rain must be at most 500')
      .typeError('freebets per rain must be a number'),
    rainsPerDay: number()
      .required('rains Per Day is required')
      .integer('rain counts per day must be an integer')
      .positive('rain counts per day must be positive')
      .typeError('rain counts per day must be a number')
      .test(
        'min-interval-between-rains',
        'The interval between rains must be at least 5 minutes. Increase the distribution window or decrease the number of rains per day.',
        function validateMinInterval(value) {
          const { distributionFrom, distributionTo } = this
            .parent as Partial<CreateRaindropFormState>;
          const fromMinutes = parseHHMMToMinutes(distributionFrom);
          const toMinutes = parseHHMMToMinutes(distributionTo);

          if (fromMinutes === null || toMinutes === null || !value || value <= 0) {
            return true;
          }

          const intervalMinutes = toMinutes - fromMinutes;
          if (intervalMinutes <= 0) {
            return true;
          }

          const intervalPerRain = intervalMinutes / value;
          return intervalPerRain >= 5;
        }
      ),
    freebetValue: number()
      .required('freebet value is required')
      .positive('freebet value must be positive')
      .typeError('freebet value must be a number')
      .test(
        'provider-and-currency-required',
        'First fill Provider and Currency fields',
        function (value) {
          const { providerId, currency } = this.parent as Partial<CreateRaindropFormState>;
          if (!value || value === null || value === undefined) {
            return true;
          }
          if (!providerId || !currency) {
            return false;
          }
          return true;
        }
      )
      .test('freebet-value-range', function (value) {
        const { providerId, currency } = this.parent as Partial<CreateRaindropFormState>;

        if (!value || value === null || value === undefined) {
          return true;
        }

        if (!providerId || !currency || !providerConfigs || providerConfigs.length === 0) {
          return true;
        }

        const config = providerConfigs.find((config) => config.currency === currency);

        if (!config) {
          return true;
        }

        if (value < config.minBet) {
          return this.createError({
            message: `Freebet value must be at least ${config.minBet}`,
          });
        }

        if (value > config.maxBet) {
          return this.createError({
            message: `Freebet value must be at most ${config.maxBet}`,
          });
        }

        return true;
      })
      .test('freebet-value-roundable', function (value) {
        const { providerId, currency } = this.parent as Partial<CreateRaindropFormState>;

        if (!value || value === null || value === undefined) {
          return true;
        }

        if (!providerId || !currency || !providerConfigs || providerConfigs.length === 0) {
          return true;
        }

        const config = providerConfigs.find((config) => config.currency === currency);

        if (!config) {
          return true;
        }

        if (config.isRoundable && !Number.isInteger(value)) {
          return this.createError({
            message: 'Freebet value must be an integer for this currency',
          });
        }

        return true;
      }),
    currency: string().required('currency is required'),
    claimLimitNumber: number()
      .required('claim limit number is required')
      .integer('claim limit number must be an integer')
      .typeError('claim limit number must be a number'),
    claimLimitPeriod: string()
      .nullable()
      .notRequired()
      .typeError('claim limit period must be a string'),
    minMultiplier: number()
      .required('min multiplier is required')
      .positive('min multiplier must be positive')
      .typeError('min multiplier must be a number')
      .test(
        'provider-and-currency-required',
        'First fill Provider and Currency fields',
        function (value) {
          const { providerId, currency } = this.parent as Partial<CreateRaindropFormState>;
          if (!value || value === null || value === undefined) {
            return true;
          }
          if (!providerId || !currency) {
            return false;
          }
          return true;
        }
      )
      .test('min-multiplier-range', function (value) {
        const { providerId, currency } = this.parent as Partial<CreateRaindropFormState>;

        if (!value || value === null || value === undefined) {
          return true;
        }

        if (!providerId || !currency || !providerConfigs || providerConfigs.length === 0) {
          return true;
        }

        const config = providerConfigs.find((config) => config.currency === currency);

        if (!config) {
          return true;
        }

        if (
          config.minAutoCashout === null ||
          config.minAutoCashout === undefined ||
          config.maxAutoCashout === null ||
          config.maxAutoCashout === undefined
        ) {
          return true;
        }

        if (value < config.minAutoCashout) {
          return this.createError({
            message: `Min multiplier must be at least ${config.minAutoCashout}`,
          });
        }

        if (value > config.maxAutoCashout) {
          return this.createError({
            message: `Min multiplier must be at most ${config.maxAutoCashout}`,
          });
        }

        return true;
      }),
    freebetValidityDate: date()
      .nullable()
      .notRequired()
      .typeError('freebet validity date must be a valid date')
      .test(
        'is-after-end-date',
        'Freebet validity date must be later than end date',
        function (value) {
          const { endDate } = this.parent as Partial<CreateRaindropFormState>;

          if (!value || !endDate) {
            return true;
          }

          const validityDate = new Date(value);
          const campaignEndDate = new Date(endDate);

          const validityDateOnly = new Date(
            validityDate.getFullYear(),
            validityDate.getMonth(),
            validityDate.getDate()
          );
          const endDateOnly = new Date(
            campaignEndDate.getFullYear(),
            campaignEndDate.getMonth(),
            campaignEndDate.getDate()
          );

          return validityDateOnly > endDateOnly;
        }
      ),
    claimDeadlineMinutes: number()
      .required('claim deadline is required')
      .integer('claim deadline must be an integer')
      .typeError('claim deadline must be a number'),
    playerMinBalance: number()
      .nullable()
      .notRequired()
      .typeError('player min balance must be a number'),
    playerMinWager: number()
      .nullable()
      .notRequired()
      .transform((value, originalValue) => {
        if (
          originalValue === '' ||
          originalValue === null ||
          originalValue === undefined ||
          (typeof originalValue === 'string' && originalValue.trim() === '')
        ) {
          return null;
        }
        if (typeof value === 'number' && Number.isNaN(value)) {
          return null;
        }
        return value;
      })
      .typeError('playerMinWager must be a number')
      .test(
        'required-when-wager-period-set',
        'player min wager is required when wager period is set',
        function (value) {
          const { wagerPeriod } = this.parent as Partial<CreateRaindropFormState>;
          if (!isEmptyValue(wagerPeriod)) {
            return !isEmptyValue(value);
          }
          return true;
        }
      ),
    wagerPeriod: number()
      .nullable()
      .notRequired()
      .transform((value, originalValue) => {
        if (
          originalValue === '' ||
          originalValue === null ||
          originalValue === undefined ||
          (typeof originalValue === 'string' && originalValue.trim() === '')
        ) {
          return null;
        }
        if (typeof value === 'number' && Number.isNaN(value)) {
          return null;
        }
        return value;
      })
      .typeError('wager period must be a number')
      .test(
        'required-when-player-min-wager-set',
        'wager period is required when player min wager is set',
        function (value) {
          const { playerMinWager } = this.parent as Partial<CreateRaindropFormState>;
          if (!isEmptyValue(playerMinWager)) {
            return !isEmptyValue(value);
          }
          return true;
        }
      ),
    dayBudget: number().required('day budget is required').typeError('day budget must be a number'),
    totalBudget: number()
      .required('total budget is required')
      .typeError('total budget must be a number'),
  });
};

export const createRaindropSchemaDefault: ObjectSchema<Partial<CreateRaindropFormState>> =
  createRaindropSchema();

type CreateRaindropFormContextType = {
  schema: ObjectSchema<Partial<CreateRaindropFormState>>;
  setSchema: Dispatch<ObjectSchema<Partial<CreateRaindropFormState>>>;
  methods?: UseFormReturn<CreateRaindropFormState>;
};

const createRaindropFormContextDefault = {
  schema: createRaindropSchemaDefault,
  setSchema: noop,
} satisfies CreateRaindropFormContextType;

export const CreateRaindropFormContext = createContext<CreateRaindropFormContextType>(
  createRaindropFormContextDefault
);

export const useCreateRaindropForm = () => {
  const { methods } = useContext(CreateRaindropFormContext);
  if (!methods) {
    throw new Error('CreateRaindropFormContext is empty');
  }

  return methods;
};

export const useManageCreateRaindropSchema = () => {
  const { setSchema } = useContext(CreateRaindropFormContext);

  return { setSchema };
};
